export const statementWorkspaceEn = {
  intro:
    "Reconcile customer balances from proforma invoices and their allocated receipts. Invoice links are references to the source PI.",
  adjustmentsHelp:
    "Net receipts include voided receipts and can be negative. Adjustments reflect PI archive scope changes, not credit notes.",
  preview: "Preview statement",
  previewFrame: "Statement document preview",
  previewSnapshot: "Saved report generated {{at}}",
  allCurrencies: "All currencies",
  filter: "Balance filter",
  search: "Search documents",
  searchHint: "Customer, PI or invoice number",
  mode: "Report mode",
  currentMode: "Current balances",
  asOfMode: "As of date",
  asOfDate: "Balance through end of day",
  fromDate: "Period start (optional)",
  sort: "Document order",
  reset: "Reset filters",
  periodHelp:
    "Period start defines the opening balance; older PIs remain included. Leave it blank for cumulative balances. Dates use your local time zone.",
  filteredScope:
    "Totals and exports cover only the documents matching these filters; they may not represent the full customer account.",
  customerError: "Customer options could not be loaded.",
  outputError: "The statement could not be prepared. Please try again.",
  invalidDates:
    "Choose a valid as-of date no later than today, with period start on or before the report date.",
  loading: "Loading balances for the selected filters…",
  reportAt: "Balances as of {{at}}",
  generatedAt: "Generated {{at}}",
  basisTitle: "Report basis and limitations",
  historyBasis:
    "This report applies dated receipts to the current PI values, dates, terms and customer details. Past PI edits are not fully reconstructed; this is not an immutable historical ledger.",
  currentBasis:
    "Balances use current PI values and allocated receipts. A linked invoice is a reference and is not counted again.",
  dueBasis:
    "Aging uses PI delivery date plus recognized payment terms. Missing or unrecognized inputs remain unaged. “After statement” terms require review of the delivery-date basis.",
  historyLimited:
    "Some source records have limited historical evidence. Review flagged rows before using this report for reconciliation.",
  empty: "No documents match these filters",
  emptyHint:
    "Try another customer, balance filter or date. An empty result does not necessarily mean every account is settled.",
  groupCount: "{{count}} customer groups",
  netHelp:
    "Net balance = gross receivable − customer credits. Credits do not reduce the overdue amounts of other PIs.",
  periodEquation:
    "Opening + period billed − period receipts + adjustments = closing net balance",
  unagedHelp:
    "Unaged receivable: {{amount}}. Aging includes positive balances only; settled and credit PIs are excluded.",
  documents: "{{count}} source PIs",
  unknownCustomer: "Unassigned customer",
  linkedInvoice: "Linked invoice · reference",
  archived: "Archived",
  terms: "Payment terms",
  dueDate: "Due date",
  opening: "Opening balance",
  periodBilled: "Period billed",
  periodPaid: "Period receipts",
  adjustments: "Adjustments",
  netBalance: "Net balance",
  settlementLabel: "Settlement",
  agingLabel: "Aging",
  reviewLabel: "Review notes",
  notApplicable: "Not applicable",
  lineHistoryLimited: "Historical source requires review.",
  filters: {
    all: "All documents",
    outstanding: "Outstanding receivables",
    overdue: "Overdue receivables",
    settled: "Settled",
    credit: "Customer credits",
    review: "Needs review",
  },
  sorts: {
    oldest: "PI date · oldest first",
    newest: "PI date · newest first",
    due: "Due date · earliest first",
    balance: "Net balance · highest first",
  },
  buckets: {
    current: "Not yet overdue",
    days1to30: "1–30 days",
    days31to60: "31–60 days",
    days61to90: "61–90 days",
    days90plus: "Over 90 days",
    unknown: "Unaged",
  },
  settlement: {
    credit: "Credit",
    settled: "Settled",
    partial: "Part paid",
    unpaid: "Unpaid",
  },
  dueReasons: {
    missingTerms: "Missing payment terms; due date is unknown.",
    unknownTerms: "Unrecognized payment terms; due date is unknown.",
    missingDelivery: "Missing PI delivery date; due date is unknown.",
    statementTermsReview:
      "“After statement” terms: review the PI delivery-date basis.",
  },
  totals: {
    receivable: "Gross receivable",
    credit: "Customer credits",
    totalBalance: "Net balance",
    overdueAmount: "Overdue receivable",
  },
  periodTotals: {
    openingBalance: "Opening balance",
    periodBilled: "Period billed",
    periodPaid: "Period receipts",
    periodAdjustments: "Adjustments",
    totalBalance: "Closing net balance",
  },
  csv_scope: "Report scope",
  csv_customerScope: "Customer statement",
  csv_internalScope: "Internal report — all customers in selected scope",
  csv_cutoff: "Cutoff (UTC timestamp)",
  csv_start: "Period start (UTC timestamp)",
  csv_allHistory: "All available recorded history",
  csv_generated: "Generated (UTC timestamp)",
  csv_mode: "Report mode",
  csv_filter: "Balance filter",
  csv_search: "Search",
  csv_allDocuments: "All matching documents",
  csv_sort: "Sort order",
  csv_sort_date: "PI date, oldest first",
  "csv_sort_-date": "PI date, newest first",
  csv_sort_due: "Due date, earliest first",
  "csv_sort_-balance": "Closing balance, highest first",
  csv_customerId: "Customer ID",
  csv_allCustomers: "All customers",
  csv_currency: "Currency",
  csv_separateCurrencies: "All currencies, reported separately",
  csv_timezone: "Report timezone offset (minutes west of UTC)",
  csv_source: "Receivable source",
  csv_basis: "Document basis warning",
  currentBasisWarning:
    "Current PI values and terms are used; prior header edits are not reconstructed. Invoices are reference only and do not create an additional receivable.",
  csv_history: "History assessment",
  csv_historyLimited:
    "Recorded history is incomplete or uncertain; verify opening balances with source records.",
  csv_historyAvailable:
    "Based on available recorded payment history; not a reconstruction of prior PI edits.",
  csv_reconciliation: "Reconciliation basis",
  csv_reconciliationNote:
    "Opening + period billed - net receipts + scope adjustments = closing. Scope adjustments reflect archived PI removal, not credit notes. Voids can make net receipts negative. Opening uses the same selected document scope.",
  csv_metadata: "Report metadata",
  csv_value: "Value",
  csv_customer: "Customer",
  csv_opening: "Opening balance",
  csv_periodBilled: "Period billed",
  csv_periodPaid: "Net receipts in period",
  csv_adjustments: "Scope adjustments (archive)",
  csv_closing: "Closing net balance",
  csv_receivable: "Receivable",
  csv_credit: "Credit balance",
  csv_overdue: "Overdue receivable",
  csv_overdueCount: "Overdue PI count",
  csv_unaged: "Unaged receivable",
  csv_billed: "PI value at cutoff (current document basis)",
  csv_paid: "Payments at cutoff",
  csv_piId: "PI ID",
  csv_piNo: "PI number",
  csv_date: "PI date (UTC timestamp)",
  csv_invoiceReference: "Invoice reference only",
  csv_invoiceArchived: "Invoice archived",
  csv_archived: "PI archived at cutoff",
  csv_state: "Balance status",
  csv_delivery: "PI delivery date (UTC timestamp)",
  csv_terms: "PI payment terms",
  csv_due: "Due date (UTC timestamp)",
  csv_dueBasis: "Due-date basis",
  csv_piDelivery: "PI delivery + payment terms (legacy basis)",
  csv_unknown: "Unknown — review required",
  csv_dueReason: "Due-date review reason",
  csv_age: "Age in days at cutoff",
  csv_overdueDays: "Overdue days (positive receivables only)",
  csv_bucket: "Aging bucket",
  csv_historyUncertain: "History requires review",
  csv_documentCount: "PI count",
  mode_current: "Current snapshot",
  mode_asOf: "Historical as-of snapshot",
  filter_all: "All balances",
  filter_outstanding: "Outstanding receivables",
  filter_overdue: "Overdue receivables",
  filter_settled: "Settled",
  filter_credit: "Credit balances",
  filter_review: "Needs review",
  state_archived: "Archived (scope removed)",
  state_settled: "Settled",
  state_credit: "Credit",
  state_review: "Review",
  state_overdue: "Overdue",
  state_current: "Current",
  csv_reason_missingTerms: "Payment terms missing",
  csv_reason_unknownTerms: "Payment terms not recognized",
  csv_reason_missingDelivery: "PI delivery date missing",
  csv_reason_statementTermsReview:
    "Statement-based terms require review; legacy PI delivery basis retained",
  roundingBasis:
    "Each PI total and its aggregated receipts are rounded to 2 decimals for reporting. Balances reconcile to these displayed values; stored source amounts remain unchanged.",
  csv_rounding: "Rounding basis",
};

export const statementWorkspaceVi = {
  intro:
    "Đối chiếu công nợ khách hàng từ PI và các khoản thu đã phân bổ. Liên kết hóa đơn là tham chiếu của PI gốc.",
  adjustmentsHelp:
    "Thu thuần gồm khoản thu bị hủy và có thể âm. Điều chỉnh phản ánh thay đổi phạm vi do lưu trữ PI, không phải giấy báo có.",
  preview: "Xem trước bảng kê",
  previewFrame: "Bản xem trước chứng từ đối chiếu",
  previewSnapshot: "Báo cáo đã chụp lúc {{at}}",
  allCurrencies: "Tất cả loại tiền",
  filter: "Lọc công nợ",
  search: "Tìm chứng từ",
  searchHint: "Khách hàng, số PI hoặc hóa đơn",
  mode: "Chế độ báo cáo",
  currentMode: "Công nợ hiện tại",
  asOfMode: "Tại ngày",
  asOfDate: "Công nợ đến hết ngày",
  fromDate: "Đầu kỳ (không bắt buộc)",
  sort: "Thứ tự chứng từ",
  reset: "Đặt lại bộ lọc",
  periodHelp:
    "Đầu kỳ xác định số dư đầu kỳ; PI cũ vẫn được tính. Để trống để xem lũy kế. Ngày dùng múi giờ của bạn.",
  filteredScope:
    "Tổng số và bản xuất chỉ gồm chứng từ khớp bộ lọc; có thể không phản ánh toàn bộ tài khoản khách hàng.",
  customerError: "Không tải được danh sách khách hàng.",
  outputError: "Không thể tạo bảng kê. Vui lòng thử lại.",
  invalidDates:
    "Chọn ngày báo cáo hợp lệ không sau hôm nay và đầu kỳ không sau ngày báo cáo.",
  loading: "Đang tải công nợ theo bộ lọc đã chọn…",
  reportAt: "Công nợ tại {{at}}",
  generatedAt: "Tạo lúc {{at}}",
  basisTitle: "Cơ sở và giới hạn báo cáo",
  historyBasis:
    "Báo cáo áp dụng khoản thu theo ngày vào giá trị, ngày, điều khoản và thông tin khách hàng hiện tại của PI. Chưa khôi phục đầy đủ các chỉnh sửa PI trong quá khứ; đây không phải sổ lịch sử bất biến.",
  currentBasis:
    "Công nợ dùng giá trị PI hiện tại và các khoản thu đã phân bổ. Hóa đơn liên kết chỉ là tham chiếu, không được tính thêm.",
  dueBasis:
    "Tuổi nợ tính từ ngày giao trên PI cộng số ngày của điều khoản nhận diện được. Thiếu hoặc không nhận diện được dữ liệu thì chưa xác định tuổi nợ. Điều khoản “sau bảng kê” cần rà soát mốc ngày giao.",
  historyLimited:
    "Một số chứng từ thiếu bằng chứng lịch sử. Rà soát các dòng được đánh dấu trước khi dùng báo cáo để đối chiếu.",
  empty: "Không có chứng từ khớp bộ lọc",
  emptyHint:
    "Thử khách hàng, bộ lọc công nợ hoặc ngày khác. Kết quả trống không đồng nghĩa mọi tài khoản đã tất toán.",
  groupCount: "{{count}} nhóm khách hàng",
  netHelp:
    "Công nợ thuần = phải thu gộp − dư có khách hàng. Dư có không làm giảm khoản quá hạn của PI khác.",
  periodEquation:
    "Đầu kỳ + phát sinh PI − thu trong kỳ + điều chỉnh = công nợ thuần cuối kỳ",
  unagedHelp:
    "Phải thu chưa xác định tuổi nợ: {{amount}}. Tuổi nợ chỉ gồm dư phải thu dương; loại PI đã tất toán và dư có.",
  documents: "{{count}} PI nguồn",
  unknownCustomer: "Chưa xác định khách hàng",
  linkedInvoice: "Hóa đơn liên kết · tham chiếu",
  archived: "Đã lưu trữ",
  terms: "Điều khoản thanh toán",
  dueDate: "Ngày đến hạn",
  opening: "Số dư đầu kỳ",
  periodBilled: "PI trong kỳ",
  periodPaid: "Thu trong kỳ",
  adjustments: "Điều chỉnh",
  netBalance: "Công nợ thuần",
  settlementLabel: "Thanh toán",
  agingLabel: "Tuổi nợ",
  reviewLabel: "Ghi chú rà soát",
  notApplicable: "Không áp dụng",
  lineHistoryLimited: "Cần rà soát nguồn lịch sử.",
  filters: {
    all: "Tất cả chứng từ",
    outstanding: "Còn phải thu",
    overdue: "Phải thu quá hạn",
    settled: "Đã tất toán",
    credit: "Khách hàng dư có",
    review: "Cần rà soát",
  },
  sorts: {
    oldest: "Ngày PI · cũ nhất trước",
    newest: "Ngày PI · mới nhất trước",
    due: "Ngày đến hạn · sớm nhất trước",
    balance: "Công nợ thuần · cao nhất trước",
  },
  buckets: {
    current: "Chưa quá hạn",
    days1to30: "1–30 ngày",
    days31to60: "31–60 ngày",
    days61to90: "61–90 ngày",
    days90plus: "Trên 90 ngày",
    unknown: "Chưa xác định",
  },
  settlement: {
    credit: "Dư có",
    settled: "Đã tất toán",
    partial: "Thu một phần",
    unpaid: "Chưa thu",
  },
  dueReasons: {
    missingTerms: "Thiếu điều khoản thanh toán; chưa xác định ngày đến hạn.",
    unknownTerms:
      "Chưa nhận diện điều khoản thanh toán; chưa xác định ngày đến hạn.",
    missingDelivery: "Thiếu ngày giao trên PI; chưa xác định ngày đến hạn.",
    statementTermsReview:
      "Điều khoản “sau bảng kê”: rà soát mốc ngày giao trên PI.",
  },
  totals: {
    receivable: "Phải thu gộp",
    credit: "Dư có khách hàng",
    totalBalance: "Công nợ thuần",
    overdueAmount: "Phải thu quá hạn",
  },
  periodTotals: {
    openingBalance: "Số dư đầu kỳ",
    periodBilled: "PI trong kỳ",
    periodPaid: "Thu trong kỳ",
    periodAdjustments: "Điều chỉnh",
    totalBalance: "Công nợ thuần cuối kỳ",
  },
  csv_scope: "Phạm vi báo cáo",
  csv_customerScope: "Bảng kê khách hàng",
  csv_internalScope: "Báo cáo nội bộ — mọi khách hàng trong phạm vi đã chọn",
  csv_cutoff: "Thời điểm chốt (UTC)",
  csv_start: "Đầu kỳ (UTC)",
  csv_allHistory: "Toàn bộ lịch sử đã ghi nhận hiện có",
  csv_generated: "Thời điểm tạo (UTC)",
  csv_mode: "Chế độ báo cáo",
  csv_filter: "Bộ lọc số dư",
  csv_search: "Tìm kiếm",
  csv_allDocuments: "Mọi chứng từ phù hợp",
  csv_sort: "Thứ tự sắp xếp",
  csv_sort_date: "Ngày PI, cũ nhất trước",
  "csv_sort_-date": "Ngày PI, mới nhất trước",
  csv_sort_due: "Hạn thanh toán, sớm nhất trước",
  "csv_sort_-balance": "Dư cuối kỳ, cao nhất trước",
  csv_customerId: "Mã định danh khách hàng",
  csv_allCustomers: "Mọi khách hàng",
  csv_currency: "Tiền tệ",
  csv_separateCurrencies: "Mọi tiền tệ, báo cáo riêng biệt",
  csv_timezone: "Độ lệch múi giờ báo cáo (phút về phía tây UTC)",
  csv_source: "Nguồn công nợ",
  csv_basis: "Lưu ý cơ sở chứng từ",
  currentBasisWarning:
    "Sử dụng giá trị và điều khoản PI hiện tại; không tái dựng các lần sửa thông tin đầu chứng từ trước đây. Hóa đơn chỉ để tham chiếu, không tạo thêm công nợ.",
  csv_history: "Đánh giá lịch sử",
  csv_historyLimited:
    "Lịch sử ghi nhận chưa đầy đủ hoặc chưa chắc chắn; đối chiếu dư đầu kỳ với chứng từ nguồn.",
  csv_historyAvailable:
    "Dựa trên lịch sử thanh toán đã ghi nhận hiện có; không tái dựng các lần sửa PI trước đây.",
  csv_reconciliation: "Cơ sở đối chiếu",
  csv_reconciliationNote:
    "Đầu kỳ + phát sinh PI - thu ròng + điều chỉnh phạm vi = cuối kỳ. Điều chỉnh phạm vi phản ánh PI bị lưu trữ, không phải phiếu giảm công nợ. Hủy thanh toán có thể khiến thu ròng âm. Dư đầu kỳ dùng cùng phạm vi chứng từ đã chọn.",
  csv_metadata: "Thông tin phạm vi báo cáo",
  csv_value: "Giá trị",
  csv_customer: "Khách hàng",
  csv_opening: "Dư đầu kỳ",
  csv_periodBilled: "Phát sinh PI trong kỳ",
  csv_periodPaid: "Thu ròng trong kỳ",
  csv_adjustments: "Điều chỉnh phạm vi (lưu trữ)",
  csv_closing: "Dư ròng cuối kỳ",
  csv_receivable: "Phải thu",
  csv_credit: "Dư có",
  csv_overdue: "Phải thu quá hạn",
  csv_overdueCount: "Số PI quá hạn",
  csv_unaged: "Phải thu chưa xác định tuổi nợ",
  csv_billed: "Giá trị PI tại thời điểm chốt (cơ sở chứng từ hiện tại)",
  csv_paid: "Đã thu tại thời điểm chốt",
  csv_piId: "Mã định danh PI",
  csv_piNo: "Số PI",
  csv_date: "Ngày PI (UTC)",
  csv_invoiceReference: "Hóa đơn tham chiếu",
  csv_invoiceArchived: "Hóa đơn đã lưu trữ",
  csv_archived: "PI đã lưu trữ tại thời điểm chốt",
  csv_state: "Trạng thái số dư",
  csv_delivery: "Ngày giao hàng PI (UTC)",
  csv_terms: "Điều khoản thanh toán PI",
  csv_due: "Hạn thanh toán (UTC)",
  csv_dueBasis: "Cơ sở tính hạn thanh toán",
  csv_piDelivery: "Ngày giao PI + điều khoản thanh toán (cơ sở cũ)",
  csv_unknown: "Chưa rõ — cần kiểm tra",
  csv_dueReason: "Lý do cần kiểm tra hạn thanh toán",
  csv_age: "Tuổi nợ (ngày) tại thời điểm chốt",
  csv_overdueDays: "Số ngày quá hạn (chỉ khoản phải thu dương)",
  csv_bucket: "Nhóm tuổi nợ",
  csv_historyUncertain: "Lịch sử cần kiểm tra",
  csv_documentCount: "Số PI",
  mode_current: "Ảnh chụp hiện tại",
  mode_asOf: "Ảnh chụp tại thời điểm lịch sử",
  filter_all: "Mọi số dư",
  filter_outstanding: "Còn phải thu",
  filter_overdue: "Phải thu quá hạn",
  filter_settled: "Đã tất toán",
  filter_credit: "Dư có",
  filter_review: "Cần kiểm tra",
  state_archived: "Đã lưu trữ (loại khỏi phạm vi)",
  state_settled: "Đã tất toán",
  state_credit: "Dư có",
  state_review: "Cần kiểm tra",
  state_overdue: "Quá hạn",
  state_current: "Chưa quá hạn",
  csv_reason_missingTerms: "Thiếu điều khoản thanh toán",
  csv_reason_unknownTerms: "Chưa nhận diện được điều khoản thanh toán",
  csv_reason_missingDelivery: "Thiếu ngày giao hàng PI",
  csv_reason_statementTermsReview:
    "Điều khoản theo bảng kê cần kiểm tra; giữ cơ sở ngày giao PI cũ",
  roundingBasis:
    "Tổng từng PI và tổng tiền thu của PI được làm tròn 2 chữ số khi báo cáo. Số dư khớp các giá trị hiển thị này; số tiền nguồn đã lưu không bị thay đổi.",
  csv_rounding: "Cơ sở làm tròn",
};

export const statementWorkspaceZh = {
  intro: "依据形式发票及其已分配收款核对客户余额。发票关联仅为来源 PI 的参考。",
  adjustmentsHelp:
    "净收款包含作废收款，可能为负数。调整反映 PI 归档造成的范围变动，并非贷项通知单。",
  preview: "预览对账单",
  previewFrame: "对账单文件预览",
  previewSnapshot: "已保存的报表生成于 {{at}}",
  allCurrencies: "所有币种",
  filter: "余额筛选",
  search: "搜索单据",
  searchHint: "客户、PI 或发票号码",
  mode: "报表模式",
  currentMode: "目前余额",
  asOfMode: "截至日期",
  asOfDate: "截至当日终了的余额",
  fromDate: "期间起日（选填）",
  sort: "单据排序",
  reset: "重置筛选",
  periodHelp:
    "期间起日用于计算期初余额，较早的 PI 仍会列入。留空可查看累计余额。日期使用您的本地时区。",
  filteredScope: "总额与导出仅包含符合筛选的单据，可能不代表客户的完整账户。",
  customerError: "无法加载客户选项。",
  outputError: "无法准备对账单，请重试。",
  invalidDates: "请选择不晚于今天的有效截止日期，且期间起日不得晚于报表日期。",
  loading: "正在加载所选筛选条件的余额…",
  reportAt: "截至 {{at}} 的余额",
  generatedAt: "生成于 {{at}}",
  basisTitle: "报表依据与限制",
  historyBasis:
    "本报表将收款按日期应用于 PI 目前的金额、日期、条款及客户信息。过往 PI 修改未完整重建，因此并非不可变更的历史账本。",
  currentBasis:
    "余额使用 PI 目前金额及已分配收款。关联发票仅供参考，不重复计算。",
  dueBasis:
    "账龄依 PI 交货日加上可识别的付款条件天数计算。数据缺失或无法识别时不计账龄。“对账单后”条款须复核以交货日计算的依据。",
  historyLimited:
    "部分来源单据的历史证据有限。用于对账前，请复核已标示的明细。",
  empty: "没有符合筛选条件的单据",
  emptyHint:
    "请尝试其他客户、余额筛选或日期。结果为空不一定表示所有账户均已结清。",
  groupCount: "{{count}} 个客户分组",
  netHelp:
    "净余额 = 应收总额 − 客户贷方余额。贷方余额不会扣减其他 PI 的逾期金额。",
  periodEquation: "期初 + 本期开单 − 本期收款 + 调整 = 期末净余额",
  unagedHelp:
    "未分类账龄应收：{{amount}}。账龄仅包含正数余额，不包含已结清及贷方余额 PI。",
  documents: "{{count}} 张来源 PI",
  unknownCustomer: "未指定客户",
  linkedInvoice: "关联发票 · 参考",
  archived: "已归档",
  terms: "付款条件",
  dueDate: "到期日",
  opening: "期初余额",
  periodBilled: "本期开单",
  periodPaid: "本期收款",
  adjustments: "调整",
  netBalance: "净余额",
  settlementLabel: "结清状态",
  agingLabel: "账龄",
  reviewLabel: "复核备注",
  notApplicable: "不适用",
  lineHistoryLimited: "历史来源须复核。",
  filters: {
    all: "所有单据",
    outstanding: "未收应收款",
    overdue: "逾期应收款",
    settled: "已结清",
    credit: "客户贷方余额",
    review: "待复核",
  },
  sorts: {
    oldest: "PI 日期 · 最早优先",
    newest: "PI 日期 · 最新优先",
    due: "到期日 · 最早优先",
    balance: "净余额 · 最高优先",
  },
  buckets: {
    current: "尚未逾期",
    days1to30: "1–30 天",
    days31to60: "31–60 天",
    days61to90: "61–90 天",
    days90plus: "超过 90 天",
    unknown: "未分类账龄",
  },
  settlement: {
    credit: "贷方余额",
    settled: "已结清",
    partial: "部分收款",
    unpaid: "未收款",
  },
  dueReasons: {
    missingTerms: "缺少付款条件，无法确定到期日。",
    unknownTerms: "无法识别付款条件，无法确定到期日。",
    missingDelivery: "缺少 PI 交货日，无法确定到期日。",
    statementTermsReview: "“对账单后”条款：请复核以 PI 交货日计算的依据。",
  },
  totals: {
    receivable: "应收总额",
    credit: "客户贷方余额",
    totalBalance: "净余额",
    overdueAmount: "逾期应收款",
  },
  periodTotals: {
    openingBalance: "期初余额",
    periodBilled: "本期开单",
    periodPaid: "本期收款",
    periodAdjustments: "调整",
    totalBalance: "期末净余额",
  },
  csv_scope: "报表范围",
  csv_customerScope: "客户对账单",
  csv_internalScope: "内部报表 — 所选范围内所有客户",
  csv_cutoff: "截止时间（UTC）",
  csv_start: "期间开始（UTC）",
  csv_allHistory: "所有可用的已记录历史",
  csv_generated: "生成时间（UTC）",
  csv_mode: "报表模式",
  csv_filter: "余额筛选",
  csv_search: "搜索",
  csv_allDocuments: "所有符合的单据",
  csv_sort: "排序",
  csv_sort_date: "PI 日期，由旧到新",
  "csv_sort_-date": "PI 日期，由新到旧",
  csv_sort_due: "到期日，由早到晚",
  "csv_sort_-balance": "期末余额，由高到低",
  csv_customerId: "客户 ID",
  csv_allCustomers: "所有客户",
  csv_currency: "币种",
  csv_separateCurrencies: "所有币种，分别列示",
  csv_timezone: "报表时区偏移（UTC 以西分钟）",
  csv_source: "应收账款来源",
  csv_basis: "单据依据提醒",
  currentBasisWarning:
    "使用目前 PI 金额及条款；不重建先前的单据表头修改。发票仅供参考，不会产生额外应收账款。",
  csv_history: "历史记录评估",
  csv_historyLimited: "已记录的历史不完整或不确定；请与原始记录核对期初余额。",
  csv_historyAvailable: "依据可用的已记录付款历史；并非重建先前的 PI 修改。",
  csv_reconciliation: "对账依据",
  csv_reconciliationNote:
    "期初 + 期间开单 - 净收款 + 范围调整 = 期末。范围调整反映归档 PI 的移除，并非贷项通知单。作废付款可能使净收款为负值。期初采用相同的所选单据范围。",
  csv_metadata: "报表元数据",
  csv_value: "值",
  csv_customer: "客户",
  csv_opening: "期初余额",
  csv_periodBilled: "期间开单金额",
  csv_periodPaid: "期间净收款",
  csv_adjustments: "范围调整（归档）",
  csv_closing: "期末净余额",
  csv_receivable: "应收账款",
  csv_credit: "贷方余额",
  csv_overdue: "逾期应收账款",
  csv_overdueCount: "逾期 PI 数量",
  csv_unaged: "账龄不明应收账款",
  csv_billed: "截止时 PI 金额（目前单据依据）",
  csv_paid: "截止时已收款",
  csv_piId: "PI ID",
  csv_piNo: "PI 编号",
  csv_date: "PI 日期（UTC）",
  csv_invoiceReference: "发票仅供参考",
  csv_invoiceArchived: "发票已归档",
  csv_archived: "截止时 PI 已归档",
  csv_state: "余额状态",
  csv_delivery: "PI 交货日期（UTC）",
  csv_terms: "PI 付款条件",
  csv_due: "到期日（UTC）",
  csv_dueBasis: "到期日依据",
  csv_piDelivery: "PI 交货日 + 付款条件（旧制依据）",
  csv_unknown: "未知 — 需要复核",
  csv_dueReason: "到期日复核原因",
  csv_age: "截止时账龄（天）",
  csv_overdueDays: "逾期天数（仅正数应收账款）",
  csv_bucket: "账龄区间",
  csv_historyUncertain: "历史记录需要复核",
  csv_documentCount: "PI 数量",
  mode_current: "目前快照",
  mode_asOf: "历史时间点快照",
  filter_all: "所有余额",
  filter_outstanding: "未收应收账款",
  filter_overdue: "逾期应收账款",
  filter_settled: "已结清",
  filter_credit: "贷方余额",
  filter_review: "需要复核",
  state_archived: "已归档（移出范围）",
  state_settled: "已结清",
  state_credit: "贷方",
  state_review: "需要复核",
  state_overdue: "逾期",
  state_current: "未逾期",
  csv_reason_missingTerms: "缺少付款条件",
  csv_reason_unknownTerms: "无法识别付款条件",
  csv_reason_missingDelivery: "缺少 PI 交货日期",
  csv_reason_statementTermsReview:
    "需复核以对账单为基础的条款；保留旧制 PI 交货日依据",
  roundingBasis:
    "报表将各 PI 总额及其收款合计四舍五入至两位小数。余额依此显示值核对；已保存来源金额维持不变。",
  csv_rounding: "四舍五入基准",
};
